import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    bookMeetingRequest,
    cancelMeetingRequest,
    getMeetingRequestMonthAvailability,
    getMeetingRequestPage,
    rescheduleMeetingRequest,
} from "./api";
import logoLight from "./assets/logo-light.svg";
import logoDark from "./assets/logo-dark.svg";
import "./PortalUI.css";
import "./ReviewPage.css";

function PublicBrand() {
    return (
        <div className="review-brand">
            <picture>
                <source srcSet={logoLight} media="(prefers-color-scheme: dark)" />
                <img src={logoDark} alt="Urban Podcasts" className="review-brand__logo" />
            </picture>
        </div>
    );
}

function getTokenFromUrl() {
    const searchToken = new URLSearchParams(window.location.search).get("token");
    if (searchToken) return searchToken;

    const hash = window.location.hash || "";
    const [, afterHash] = hash.split("#/");
    if (!afterHash) return null;
    const [, qs] = afterHash.split("?");
    if (!qs) return null;
    return new URLSearchParams(qs).get("token");
}

function formatDateTime(value, timezone = "UTC") {
    if (!value) return "Not booked";
    const raw = String(value).trim();
    const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(raw);
    const text = raw.replace(" ", "T");
    const dt = hasTimezone ? new Date(text) : new Date(`${text}Z`);
    if (Number.isNaN(dt.getTime())) return String(value);
    return dt.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: timezone,
    });
}

function buildBookingRedirectUrl(baseUrl, includeParams, email, fullName) {
    if (!baseUrl) return null;
    try {
        const url = new URL(baseUrl);
        if (includeParams) {
            const cleanEmail = String(email || "").trim();
            const compactName = String(fullName || "")
                .trim()
                .replace(/\s+/g, "");
            if (cleanEmail) url.searchParams.set("email", cleanEmail);
            if (compactName) url.searchParams.set("name", compactName);
        }
        return url.toString();
    } catch {
        return null;
    }
}

function toYmdUtc(dateObj) {
    const y = dateObj.getUTCFullYear();
    const m = String(dateObj.getUTCMonth() + 1).padStart(2, "0");
    const d = String(dateObj.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

function parseYmdToUtcDate(value) {
    const raw = String(value || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
    const dt = new Date(`${raw}T00:00:00Z`);
    if (Number.isNaN(dt.getTime())) return null;
    return dt;
}

function monthKeyFromDate(dateObj) {
    const y = dateObj.getUTCFullYear();
    const m = String(dateObj.getUTCMonth() + 1).padStart(2, "0");
    return `${y}-${m}`;
}

function formatMonthLabel(dateObj) {
    return dateObj.toLocaleDateString("en-GB", {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
    });
}

function formatSelectedDate(value) {
    const dt = parseYmdToUtcDate(value);
    if (!dt) return "";
    return dt.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "long",
        year: "numeric",
        timeZone: "UTC",
    });
}

function MeetingRequestPage() {
    const token = getTokenFromUrl();

    const [date, setDate] = useState("");
    const [loading, setLoading] = useState(true);
    const [timesLoading, setTimesLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [data, setData] = useState(null);
    const [selectedTime, setSelectedTime] = useState("");
    const [completion, setCompletion] = useState(null);
    const [calendarMonth, setCalendarMonth] = useState(() => {
        const now = new Date();
        return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    });
    const currentMonthStart = useMemo(() => {
        const now = new Date();
        return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    }, []);
    const [monthAvailability, setMonthAvailability] = useState({});
    const monthAvailabilityLoadRef = useRef(0);

    const [contactName, setContactName] = useState("");
    const [contactEmail, setContactEmail] = useState("");
    const [additionalToEmails, setAdditionalToEmails] = useState("");

    const skipNextDateLoadRef = useRef(false);

    async function loadPage(
        dateValue,
        { preservePage = false, allowAutoPickDate = false } = {}
    ) {
        if (!token) {
            setError("Missing meeting token in the link.");
            setLoading(false);
            setTimesLoading(false);
            return;
        }

        if (preservePage) {
            setTimesLoading(true);
        } else {
            setLoading(true);
        }
        setError("");

        try {
            const res = await getMeetingRequestPage(token, dateValue);
            if (!res?.ok) {
                setError(res?.message || "Failed to load booking page");
                if (!preservePage) setData(null);
            } else {
                setData(res);
                setSelectedTime("");
                setContactName(String(res?.meeting?.contact_name || ""));
                setContactEmail(String(res?.meeting?.contact_email || ""));
                setAdditionalToEmails(String(res?.meeting?.additional_to_emails || ""));

                if (
                    allowAutoPickDate &&
                    !dateValue &&
                    res?.selected_date &&
                    res.selected_date !== date
                ) {
                    skipNextDateLoadRef.current = true;
                    setDate(res.selected_date);
                }
            }
        } catch (err) {
            console.error(err);
            setError("Failed to load booking page");
            if (!preservePage) setData(null);
        } finally {
            setLoading(false);
            setTimesLoading(false);
        }
    }

    useEffect(() => {
        loadPage("", { preservePage: false, allowAutoPickDate: true });
    }, [token]);

    useEffect(() => {
        if (!date) return;
        if (skipNextDateLoadRef.current) {
            skipNextDateLoadRef.current = false;
            return;
        }
        loadPage(date, { preservePage: true, allowAutoPickDate: false });
    }, [date]);

    const hasBooking = Boolean(data?.has_booking);
    const slots = data?.available_slots || [];
    const selectedDateLabel = formatSelectedDate(date);
    const selectedDateTimeLabel =
        selectedDateLabel && selectedTime
            ? `${selectedDateLabel}, ${selectedTime}`
            : selectedDateLabel || "";

    const emailValid = useMemo(
        () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(String(contactEmail || "").trim()),
        [contactEmail]
    );

    const canSubmit = useMemo(
        () => !saving && !timesLoading && emailValid && Boolean(selectedTime),
        [saving, timesLoading, emailValid, selectedTime]
    );

    useEffect(() => {
        const selectedDate = parseYmdToUtcDate(date);
        if (!selectedDate) return;
        const selectedMonth = new Date(
            Date.UTC(selectedDate.getUTCFullYear(), selectedDate.getUTCMonth(), 1)
        );
        setCalendarMonth((prev) =>
            monthKeyFromDate(prev) === monthKeyFromDate(selectedMonth) ? prev : selectedMonth
        );
    }, [date]);

    useEffect(() => {
        if (!token || !calendarMonth) return;
        const key = monthKeyFromDate(calendarMonth);
        const existing = monthAvailability[key];
        if (existing && (existing.loading || existing.loaded)) return;

        const loadId = monthAvailabilityLoadRef.current + 1;
        monthAvailabilityLoadRef.current = loadId;
        setMonthAvailability((prev) => ({
            ...prev,
            [key]: {
                ...(prev[key] || {}),
                loading: true,
                loaded: false,
                dates: prev[key]?.dates || {},
            },
        }));

        const run = async () => {
            const res = await getMeetingRequestMonthAvailability(token, key);
            if (monthAvailabilityLoadRef.current !== loadId) return;
            setMonthAvailability((prev) => ({
                ...prev,
                [key]: {
                    loading: false,
                    loaded: true,
                    dates:
                        res?.ok && res?.dates && typeof res.dates === "object"
                            ? res.dates
                            : {},
                },
            }));
        };

        run().catch(() => {
            if (monthAvailabilityLoadRef.current !== loadId) return;
            setMonthAvailability((prev) => ({
                ...prev,
                [key]: { loading: false, loaded: true, dates: {} },
            }));
        });
    }, [token, calendarMonth, monthAvailability]);

    const monthKey = monthKeyFromDate(calendarMonth);
    const canGoToPreviousMonth = calendarMonth.getTime() > currentMonthStart.getTime();
    const monthInfo = monthAvailability[monthKey] || {
        loading: false,
        loaded: false,
        dates: {},
    };
    const monthDates = monthInfo.dates || {};
    const monthFirstWeekdaySunFirst = new Date(
        Date.UTC(calendarMonth.getUTCFullYear(), calendarMonth.getUTCMonth(), 1)
    ).getUTCDay();
    const monthLastDay = new Date(
        Date.UTC(calendarMonth.getUTCFullYear(), calendarMonth.getUTCMonth() + 1, 0)
    ).getUTCDate();
    const calendarCells = [];
    for (let i = 0; i < monthFirstWeekdaySunFirst; i += 1) {
        calendarCells.push({ kind: "pad", key: `pad-${i}` });
    }
    for (let d = 1; d <= monthLastDay; d += 1) {
        const ymd = toYmdUtc(
            new Date(Date.UTC(calendarMonth.getUTCFullYear(), calendarMonth.getUTCMonth(), d))
        );
        const available = monthDates[ymd] === true;
        const selected = ymd === date;
        calendarCells.push({
            kind: "day",
            key: ymd,
            ymd,
            day: d,
            available,
            selected,
        });
    }

    async function handleSave() {
        if (!canSubmit) return;
        setSaving(true);
        setError("");
        setCompletion(null);

        try {
            const payload = {
                date,
                time: selectedTime,
                contact_name: String(contactName || "").trim(),
                contact_email: String(contactEmail || "").trim(),
                additional_to_emails: additionalToEmails,
            };

            const res = hasBooking
                ? await rescheduleMeetingRequest(token, payload)
                : await bookMeetingRequest(token, payload);
            if (!res?.ok) {
                setError(
                    res?.message ||
                        (hasBooking
                            ? "Failed to reschedule meeting"
                            : "Failed to schedule meeting")
                );
                return;
            }

            const redirectUrl = buildBookingRedirectUrl(
                data?.settings?.booking_redirect_url,
                Boolean(data?.settings?.booking_redirect_include_params),
                contactEmail,
                contactName
            );
            if (redirectUrl) {
                window.location.assign(redirectUrl);
                return;
            }

            const confirmedAt = formatDateTime(
                res?.scheduled_at,
                data?.settings?.timezone || "Europe/London"
            );
            setCompletion({
                title: hasBooking ? "Meeting rescheduled" : "Meeting scheduled",
                summary: `Your meeting is set for ${confirmedAt}.`,
                joinUrl: res?.teams_join_url || null,
            });
        } catch (err) {
            console.error(err);
            setError(
                hasBooking ? "Failed to reschedule meeting" : "Failed to schedule meeting"
            );
        } finally {
            setSaving(false);
        }
    }

    async function handleCancel() {
        if (!hasBooking) return;
        if (!window.confirm("Cancel this meeting?")) return;

        setSaving(true);
        setError("");
        setCompletion(null);
        try {
            const res = await cancelMeetingRequest(token);
            if (!res?.ok) {
                setError(res?.message || "Failed to cancel meeting");
                return;
            }
            setCompletion({
                title: "Meeting cancelled",
                summary: "Your meeting has been cancelled.",
                joinUrl: null,
            });
        } catch (err) {
            console.error(err);
            setError("Failed to cancel meeting");
        } finally {
            setSaving(false);
        }
    }

    if (loading) {
        return (
            <div className="review-page">
                <PublicBrand />
                <div className="review-card card review-card--narrow">
                    <p>Loading meeting scheduling page...</p>
                </div>
            </div>
        );
    }

    if (error && !data) {
        return (
            <div className="review-page">
                <PublicBrand />
                <div className="review-card card review-card--narrow">
                    <h1 className="review-title">Schedule Meeting</h1>
                    <div className="alert alert--error">{error}</div>
                </div>
            </div>
        );
    }

    if (completion) {
        return (
            <div className="review-page">
                <PublicBrand />
                <div className="review-card card review-card--narrow">
                    <h1 className="review-title">{completion.title}</h1>
                    <p>{completion.summary}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="review-page">
            <PublicBrand />
            <div className="review-card card">
                <h1 className="review-title">{data?.meeting?.title || "Schedule a Meeting"}</h1>
                {data?.meeting?.description ? (
                    <p className="review-subtitle">{data.meeting.description}</p>
                ) : (
                    <p className="review-subtitle">Choose a date and time.</p>
                )}

                {error ? <div className="alert alert--error">{error}</div> : null}

                <div className="card" style={{ marginTop: 12 }}>
                    <div className="card-info-row">
                        <span className="card-info-row__label">Current status</span>
                        <span className="card-info-row__value">{data?.meeting?.status || "-"}</span>
                    </div>
                    <div className="card-info-row">
                        <span className="card-info-row__label">Current booking</span>
                        <span className="card-info-row__value">
                            {formatDateTime(
                                data?.meeting?.scheduled_at,
                                data?.settings?.timezone || "UTC"
                            )}
                        </span>
                    </div>
                    <div className="card-info-row">
                        <span className="card-info-row__label">Timezone</span>
                        <span className="card-info-row__value">
                            {data?.settings?.timezone || "Europe/London"}
                        </span>
                    </div>
                </div>

                <div className="card" style={{ marginTop: 12 }}>
                    <label className="form-field">
                        <span className="form-field__label">Your name</span>
                        <input
                            className="form-field__input"
                            value={contactName}
                            onChange={(e) => setContactName(e.target.value)}
                            disabled={saving}
                        />
                    </label>

                    <label className="form-field">
                        <span className="form-field__label">Your email</span>
                        <input
                            type="email"
                            className="form-field__input"
                            value={contactEmail}
                            onChange={(e) => setContactEmail(e.target.value)}
                            disabled={saving}
                        />
                    </label>

                    <label className="form-field">
                        <span className="form-field__label">
                            Additional attendees (comma or semicolon separated)
                        </span>
                        <textarea
                            className="form-field__textarea"
                            rows={3}
                            value={additionalToEmails}
                            onChange={(e) => setAdditionalToEmails(e.target.value)}
                            disabled={saving}
                            placeholder="name@example.com; colleague@example.com"
                        />
                    </label>

                    {!emailValid ? (
                        <div className="alert alert--error">Please enter a valid email.</div>
                    ) : null}
                </div>

                <div className="discovery-booking-layout">
                    <div className="discovery-booking-summary-wrap">
                        <div className="discovery-booking-summary-time">
                            {selectedDateTimeLabel || "Select date and time"}
                        </div>
                    </div>
                    <div className="discovery-booking-grid">
                        <div className="discovery-booking-col">
                            <div className="discovery-booking-col__title">Date</div>
                            <div className="discovery-booking-month-nav">
                                <button
                                    type="button"
                                    className="btn btn--secondary btn--small"
                                    onClick={() => {
                                        if (!canGoToPreviousMonth) return;
                                        setCalendarMonth(
                                            new Date(
                                                Date.UTC(
                                                    calendarMonth.getUTCFullYear(),
                                                    calendarMonth.getUTCMonth() - 1,
                                                    1
                                                )
                                            )
                                        );
                                    }}
                                    disabled={saving || timesLoading || !canGoToPreviousMonth}
                                >
                                    Prev
                                </button>
                                <span className="discovery-booking-month-label">
                                    {formatMonthLabel(calendarMonth)}
                                </span>
                                <button
                                    type="button"
                                    className="btn btn--secondary btn--small"
                                    onClick={() =>
                                        setCalendarMonth(
                                            new Date(
                                                Date.UTC(
                                                    calendarMonth.getUTCFullYear(),
                                                    calendarMonth.getUTCMonth() + 1,
                                                    1
                                                )
                                            )
                                        )
                                    }
                                    disabled={saving || timesLoading}
                                >
                                    Next
                                </button>
                            </div>
                            <div className="discovery-booking-weekdays">
                                {["S", "M", "T", "W", "T", "F", "S"].map((label, idx) => (
                                    <div
                                        key={`${label}-${idx}`}
                                        className="discovery-booking-weekdays__day"
                                    >
                                        {label}
                                    </div>
                                ))}
                            </div>
                            <div className="discovery-booking-days">
                                {calendarCells.map((cell) => {
                                    if (cell.kind === "pad") return <div key={cell.key} />;
                                    const isUnavailable = !cell.available;
                                    return (
                                        <button
                                            key={cell.key}
                                            type="button"
                                            onClick={() => setDate(cell.ymd)}
                                            disabled={isUnavailable || saving || timesLoading}
                                            className={
                                                "btn btn--small discovery-booking-day " +
                                                (cell.selected ? "btn--primary" : "btn--secondary") +
                                                (isUnavailable ? " discovery-booking-day--unavailable" : "")
                                            }
                                        >
                                            {cell.day}
                                        </button>
                                    );
                                })}
                            </div>
                            {monthInfo.loading ? (
                                <p className="text-description" style={{ marginTop: 8 }}>
                                    Checking available dates...
                                </p>
                            ) : null}
                        </div>

                        <div className="discovery-booking-col">
                            <div className="discovery-booking-col__title">Time</div>
                            <div className="discovery-slots-grid">
                                {timesLoading ? (
                                    <p
                                        style={{
                                            gridColumn: "1 / -1",
                                            textAlign: "center",
                                            margin: "8px 0",
                                        }}
                                    >
                                        Loading available times…
                                    </p>
                                ) : slots.length === 0 ? (
                                    <p
                                        style={{
                                            gridColumn: "1 / -1",
                                            textAlign: "center",
                                            margin: "8px 0",
                                        }}
                                    >
                                        No time slots available for this date.
                                    </p>
                                ) : (
                                    slots.map((slot) => (
                                        <button
                                            key={slot}
                                            type="button"
                                            className={
                                                "btn discovery-slot-btn " +
                                                (selectedTime === slot
                                                    ? "btn--primary"
                                                    : "btn--secondary")
                                            }
                                            disabled={timesLoading || saving}
                                            onClick={() => setSelectedTime(slot)}
                                        >
                                            {slot}
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ marginTop: 16, display: "flex", gap: 10 }}>
                    <button
                        type="button"
                        className="btn btn--primary"
                        disabled={!canSubmit}
                        onClick={handleSave}
                    >
                        {saving
                            ? "Saving..."
                            : hasBooking
                                ? "Reschedule Meeting"
                                : "Schedule Meeting"}
                    </button>
                    {hasBooking ? (
                        <button
                            type="button"
                            className="btn btn--danger"
                            disabled={saving}
                            onClick={handleCancel}
                        >
                            Cancel Meeting
                        </button>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

export default MeetingRequestPage;
